import React from "react"
import { Select } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import DataQButton from "shared/ui/Buttons/DataQButton.component"

import {
  STATISTIC_TYPE,
  CHART_DISPLAY_TYPE,
  TABLE_OPERATION_TYPE,
} from "types/ReportingDashboard/elements.types"
import { SelectOption, OperatorOption } from "types/ui.types"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { faChartColumn, faChartBar } from "@fortawesome/free-solid-svg-icons"

/**
 * Table operation option interface
 */
interface TableOperationOption {
  value: TABLE_OPERATION_TYPE
  label: string
  description: string
}

/**
 * Operator management interface (subset of useOperatorManagement return)
 */
interface OperatorManagement {
  handleOperationTypeChange: (operationType: TABLE_OPERATION_TYPE) => void
  handleStatisticTypeChange: (statisticType: STATISTIC_TYPE) => void
  availableStatisticOptions: OperatorOption[]
  tableOperationOptions: TableOperationOption[]
}

/**
 * Chart type option interface
 */
interface ChartTypeOption {
  value: CHART_DISPLAY_TYPE
  label: string
  icon: IconDefinition
}

/**
 * Chart type options constant
 */
const CHART_TYPE_OPTIONS: ChartTypeOption[] = [
  { value: CHART_DISPLAY_TYPE.BAR, label: "Bar Chart", icon: faChartColumn },
  {
    value: CHART_DISPLAY_TYPE.HORIZONTAL_BAR,
    label: "Horizontal Bar",
    icon: faChartBar,
  },
]

/**
 * Props for ChartElementVariableSelector component
 */
export interface ChartElementVariableSelectorProps {
  /** Available select options from codebook */
  selectOptions: SelectOption[]
  /** Currently selected primary variable */
  selectedVariable: string | null
  /** Currently selected breakdown variable */
  breakdownVariable: string | null
  /** Currently selected statistic type */
  selectedStatisticType: STATISTIC_TYPE
  /** Currently selected chart type */
  chartType: CHART_DISPLAY_TYPE
  /** Currently selected table operation type */
  tableOperationType: TABLE_OPERATION_TYPE
  /** Whether to show totals */
  showTotals: boolean
  /** Whether query instance is available */
  hasQueryInstance: boolean
  /** Whether component is loading */
  isLoading: boolean
  /** Whether component is queued for execution */
  isQueuedForExecution: boolean
  /** Element ID for form labels */
  elementId: string
  /** Operator management hook return */
  operatorManagement: OperatorManagement
  /** Variable selection handler */
  onVariableSelect: (value: string, type: "primary" | "breakdown") => void
  /** Chart type change handler */
  onChartTypeChange: (type: CHART_DISPLAY_TYPE) => void
  /** Show totals toggle handler */
  onShowTotalsChange: (showTotals: boolean) => void
  /** Run button click handler */
  onRun: () => void
  /** Skip next config update setter */
  setSkipNextConfigUpdate: (skip: boolean) => void
}

/**
 * Chart Variable Selector Component
 *
 * Handles the variable selection UI for chart elements including:
 * - Table operation type selection
 * - Primary and breakdown variable selection
 * - Chart type selection
 * - Statistic type selection
 * - Show totals checkbox
 * - Run button
 */
const ChartElementVariableSelector: React.FC<
  ChartElementVariableSelectorProps
> = ({
  selectOptions,
  selectedVariable,
  breakdownVariable,
  selectedStatisticType,
  chartType,
  tableOperationType,
  showTotals,
  hasQueryInstance,
  isLoading,
  isQueuedForExecution,
  elementId,
  operatorManagement,
  onVariableSelect,
  onChartTypeChange,
  onShowTotalsChange,
  onRun,
  setSkipNextConfigUpdate,
}) => {
  return (
    <div className="p-2 bg-gray-50 bg-opacity-80 rounded-md shadow-sm">
      <div className="flex flex-col space-y-1.5">
        <div>
          <label className="text-xs font-medium mb-0.5 block">
            Table Operation Type
          </label>
          <Select
            placeholder="Select operation type"
            className="w-full text-xs"
            value={tableOperationType}
            onChange={operatorManagement.handleOperationTypeChange}
            size="small"
            style={{ fontSize: "11px" }}
            dropdownStyle={{ fontSize: "11px", minWidth: "250px" }}
            optionLabelProp="label"
          >
            {operatorManagement.tableOperationOptions.map((opt) => (
              <Select.Option
                key={opt.value}
                value={opt.value}
                label={opt.label}
              >
                <div>
                  <div style={{ fontWeight: "500" }}>{opt.label}</div>
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#666",
                      marginTop: "2px",
                    }}
                  >
                    {opt.description}
                  </div>
                </div>
              </Select.Option>
            ))}
          </Select>
        </div>

        <div>
          <label className="text-xs font-medium mb-0.5 block">
            Primary Variable
          </label>
          <Select
            showSearch
            placeholder="Select primary variable"
            optionFilterProp="label"
            className="w-full text-xs"
            options={selectOptions}
            value={selectedVariable}
            onChange={(v: string) => onVariableSelect(v, "primary")}
            filterOption={(input, option) =>
              option?.label?.toLowerCase().includes(input.toLowerCase()) ??
              false
            }
            size="small"
            style={{ fontSize: "11px" }}
            dropdownStyle={{ fontSize: "11px" }}
          />
        </div>

        <div>
          <label className="text-xs font-medium mb-0.5 block">
            Breakdown Variable
          </label>
          <Select
            showSearch
            placeholder="Select breakdown variable"
            optionFilterProp="label"
            className="w-full text-xs"
            options={selectOptions}
            value={breakdownVariable}
            onChange={(v: string) => onVariableSelect(v, "breakdown")}
            filterOption={(input, option) =>
              option?.label?.toLowerCase().includes(input.toLowerCase()) ??
              false
            }
            size="small"
            style={{ fontSize: "11px" }}
            dropdownStyle={{ fontSize: "11px" }}
          />
        </div>

        <div className="flex items-end space-x-1.5">
          <div className="flex-grow">
            <label className="text-xs font-medium mb-0.5 block">
              Chart Type
            </label>
            <Select
              placeholder="Select chart type"
              className="w-full text-xs"
              value={chartType}
              onChange={onChartTypeChange}
              size="small"
              style={{ fontSize: "11px" }}
              dropdownStyle={{ fontSize: "11px" }}
            >
              {CHART_TYPE_OPTIONS.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  <div className="flex items-center">
                    <FontAwesomeIcon icon={opt.icon} className="mr-1" />
                    <span>{opt.label}</span>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className="flex-grow">
            <label className="text-xs font-medium mb-0.5 block">
              Statistic Type
            </label>
            <Select
              placeholder="Select statistic type"
              className="w-full text-xs"
              options={operatorManagement.availableStatisticOptions}
              value={selectedStatisticType}
              onChange={operatorManagement.handleStatisticTypeChange}
              size="small"
              style={{ fontSize: "11px" }}
              dropdownStyle={{ fontSize: "11px" }}
            />
          </div>
        </div>

        <div className="flex items-center mt-1">
          <input
            type="checkbox"
            id={`show-totals-checkbox-${elementId}`}
            checked={showTotals}
            onChange={(e) => onShowTotalsChange(e.target.checked)}
            className="mr-1.5"
          />
          <label
            htmlFor={`show-totals-checkbox-${elementId}`}
            className="text-xs font-medium cursor-pointer"
          >
            Show Totals
          </label>
        </div>

        <div className="flex justify-end mt-1">
          <DataQButton
            isDisabled={
              !hasQueryInstance ||
              !selectedVariable ||
              !breakdownVariable ||
              isLoading ||
              isQueuedForExecution
            }
            isLoading={isLoading || isQueuedForExecution}
            onClick={() => {
              setSkipNextConfigUpdate(false)
              onRun()
            }}
            type="primary"
            size="small"
          >
            {isQueuedForExecution ? "Queued..." : "Run"}
          </DataQButton>
        </div>
      </div>
    </div>
  )
}

export default ChartElementVariableSelector
